import { useEffect, useRef, useState } from "react";

const ModelViewerClient = ({
  glbUrl,
  usdzUrl,
  arPlacement,
  initialCameraOrbit,
  animationStartMode,
  animationPlaybackMode,
  onAnimationAvailable,
  onAnimationPlayState,
  onProgress,
  onError,
  onLoaded,
  onArSupportChange,
  onViewerReady,
}) => {
  const viewerRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !viewerRef.current) return;
    const viewer = viewerRef.current;
    let canvas = null;

    onViewerReady?.(viewer);

    const handleError = (event) => {
      console.warn("[model-viewer] error", event?.detail || event);
      onError?.(event?.detail?.message || "Błąd ładowania modelu");
    };
    const handleLoad = async () => {
      console.info("[model-viewer] load");
      onLoaded?.();
      try {
        if (viewer.updateComplete) {
          await viewer.updateComplete;
        }
        const animations = viewer.availableAnimations || [];
        console.info("[ANIMATION_3D] available", animations);
        const hasAnimations = animations.length > 0;
        onAnimationAvailable?.(hasAnimations);
        if (!hasAnimations) return;
        if (!viewer.animationName) {
          viewer.animationName = animations[0];
        }
        if (animationPlaybackMode === "loop") {
          viewer.setAttribute("animation-loop", "true");
        } else {
          viewer.removeAttribute("animation-loop");
        }
        if (animationStartMode === "auto") {
          console.info("[ANIMATION_3D] autoplay", animationPlaybackMode);
          if (animationPlaybackMode === "once") {
            viewer.play({ repetitions: 1 });
          } else {
            viewer.play();
          }
          onAnimationPlayState?.(true);
        } else {
          viewer.pause();
          onAnimationPlayState?.(false);
        }
      } catch (error) {
        console.warn("[ANIMATION_3D] init failed", error);
      }
    };
    const handleProgress = (event) => {
      console.info("[model-viewer] progress", event?.detail || event);
      onProgress?.(event);
    };
    const handleArStatus = (event) => {
      console.info("[model-viewer] ar-status", event?.detail || event);
    };

    const handleAnimationPlay = () => {
      console.info("[ANIMATION_3D] play");
      onAnimationPlayState?.(true);
    };
    const handleAnimationPause = () => {
      console.info("[ANIMATION_3D] pause");
      onAnimationPlayState?.(false);
    };
    const handleAnimationFinished = () => {
      console.info("[ANIMATION_3D] finished");
      onAnimationPlayState?.(false);
    };

    viewer.addEventListener("error", handleError);
    viewer.addEventListener("load", handleLoad);
    viewer.addEventListener("progress", handleProgress);
    viewer.addEventListener("ar-status", handleArStatus);
    viewer.addEventListener("play", handleAnimationPlay);
    viewer.addEventListener("pause", handleAnimationPause);
    viewer.addEventListener("finished", handleAnimationFinished);

    const attachCanvasListeners = () => {
      canvas = viewer.shadowRoot?.querySelector("canvas");
      if (!canvas) return;
      const handleContextLost = (event) => {
        console.warn("[model-viewer] webglcontextlost", event);
      };
      const handleContextRestored = (event) => {
        console.info("[model-viewer] webglcontextrestored", event);
      };
      canvas.addEventListener("webglcontextlost", handleContextLost);
      canvas.addEventListener("webglcontextrestored", handleContextRestored);
      canvas.__mvCleanup = () => {
        canvas.removeEventListener("webglcontextlost", handleContextLost);
        canvas.removeEventListener("webglcontextrestored", handleContextRestored);
      };
    };

    setTimeout(attachCanvasListeners, 0);

    const checkArSupport = async () => {
      try {
        if (typeof viewer.canActivateAR === "function") {
          const can = await viewer.canActivateAR();
          onArSupportChange?.(Boolean(can));
        } else if (typeof viewer.canActivateAR === "boolean") {
          onArSupportChange?.(viewer.canActivateAR);
        }
      } catch (error) {
        onArSupportChange?.(false);
      }
    };

    checkArSupport();

    return () => {
      viewer.removeEventListener("error", handleError);
      viewer.removeEventListener("load", handleLoad);
      viewer.removeEventListener("progress", handleProgress);
      viewer.removeEventListener("ar-status", handleArStatus);
      viewer.removeEventListener("play", handleAnimationPlay);
      viewer.removeEventListener("pause", handleAnimationPause);
      viewer.removeEventListener("finished", handleAnimationFinished);
      if (canvas?.__mvCleanup) {
        canvas.__mvCleanup();
      }
    };
  }, [
    mounted,
    onError,
    onLoaded,
    onArSupportChange,
    onViewerReady,
    animationStartMode,
    animationPlaybackMode,
    onAnimationAvailable,
    onAnimationPlayState,
    onProgress,
  ]);

  useEffect(() => {
    if (!mounted || !viewerRef.current || !initialCameraOrbit) return;
    viewerRef.current.cameraOrbit = initialCameraOrbit;
    console.info("[START_VIEW_3D] applied orbit", initialCameraOrbit);
  }, [mounted, initialCameraOrbit]);

  if (!mounted) {
    return (
      <div
        className="flex h-full w-full items-center justify-center text-sm text-muted-foreground"
        data-testid="model-viewer-loading"
      >
        Przygotowywanie podglądu 3D...
      </div>
    );
  }

  return (
    <model-viewer
      ref={viewerRef}
      src={glbUrl}
      ios-src={usdzUrl || undefined}
      camera-controls
      auto-rotate
      ar
      ar-placement={arPlacement === "wall" ? "wall" : undefined}
      camera-orbit={initialCameraOrbit || undefined}
      ar-modes="scene-viewer quick-look webxr"
      style={{ width: "100%", height: "100%", background: "#fdfdfd", position: "relative" }}
      data-testid="model-viewer"
    />
  );
};

export default ModelViewerClient;